import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LayoutDashboard, ClipboardList, Image, Megaphone, Users, LogOut, Loader2, LayoutGrid, Tag } from "lucide-react";

const LINKS = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testId: "admin-nav-overview" },
  { to: "/admin/orders", label: "Orders & Bookings", icon: ClipboardList, testId: "admin-nav-orders" },
  { to: "/admin/listings", label: "Listings", icon: Image, testId: "admin-nav-listings" },
  { to: "/admin/marketing", label: "Marketing Services", icon: Megaphone, testId: "admin-nav-marketing" },
  { to: "/admin/clients", label: "Clients & Team", icon: Users, testId: "admin-nav-clients" },
  { to: "/admin/site", label: "Site Content", icon: LayoutGrid, testId: "admin-nav-site" },
  { to: "/admin/discounts", label: "Discount Codes", icon: Tag, testId: "admin-nav-discounts" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }
  if (!user || !["admin", "team"].includes(user.role)) return <Navigate to="/login" replace />;

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
      isActive ? "bg-brand/10 text-white border-l-2 border-brand" : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
    }`;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-zinc-100 flex">
      <aside className="hidden md:flex flex-col w-64 border-r border-white/10 bg-[#0A0A0A] fixed inset-y-0">
        <div className="p-6 border-b border-white/10"><Logo /></div>
        <nav className="flex-1 py-4">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testId} className={navClass}>
              <l.icon className="w-4 h-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <p className="text-xs text-zinc-500 mb-1 truncate">{user.email}</p>
          <p className="text-[10px] tracking-widest uppercase text-brand mb-3">{user.role}</p>
          <button data-testid="admin-logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-red-400 transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 md:ml-64">
        <div className="md:hidden border-b border-white/10 bg-[#0A0A0A] p-3 flex items-center justify-between sticky top-0 z-40">
          <Logo compact />
          <div className="flex gap-1 overflow-x-auto">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`${l.testId}-mobile`}
                className={({ isActive }) => `px-3 py-2 text-xs whitespace-nowrap ${isActive ? "text-brand" : "text-zinc-400"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
        <main className="p-6 lg:p-10 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
